import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Card from "components/card";
import Pagination from "components/pagination/Pagination";
import Loader from "assets/img/loader.gif";
import VendorService from "services/vendor.service";
import Toast from "components/toaster";
import { FiSearch } from "react-icons/fi";
import Select from "react-select";
import { IoEye } from "react-icons/io5";
import { BsChevronDown } from "react-icons/bs";
import { AiFillEdit } from "react-icons/ai";
import { useSearchParams } from "react-router-dom";
import tabCache from "utils/tabCache";

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

function rowMenuAnchorFromTriggerRect(rect, openAbove) {
  if (openAbove) {
    return {
      placement: "above",
      triggerTop: rect.top,
      left: rect.left,
      width: rect.width,
    };
  }
  return {
    placement: "below",
    top: rect.bottom,
    left: rect.left,
    width: rect.width,
  };
}

function getRowMenuPortalStyle(anchor) {
  if (!anchor) return {};
  if (anchor.placement === "above") {
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    return {
      left: anchor.left,
      width: anchor.width,
      bottom: h - anchor.triggerTop + 4,
      top: "auto",
    };
  }
  return {
    top: anchor.top + 4,
    left: anchor.left,
    width: anchor.width,
  };
}

const colourStyles = {
  control: (styles, state) => ({
    ...styles,
    backgroundColor: "rgb(30 30 32 / 1)",
    border: "1px solid rgba(255, 255, 255, 0.1) !important",
    boxShadow: "none !important",
    "&:hover": {
      border: "1px solid rgba(255, 255, 255, 0.1) !important",
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#ffffff",
  }),
  option: (styles) => ({
    ...styles,
    backgroundColor: "rgb(30 30 32 / 1) !important",
    color: "#ffffff",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.30) !important",
      color: "#ffffff",
    },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "rgb(12 14 28 / 1)",
    borderRadius: "12px",
    border: "1px solid #2A2A2A",
    overflow: "hidden",
    marginTop: "4px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const Vendor = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");

  const dateRangeFromParams = React.useMemo(() => {
    if (!startDateParam || !endDateParam) {
      return null;
    }

    const start = new Date(startDateParam);
    const end = new Date(endDateParam);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);

    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(23, 59, 59, 999);

    return {
      startDate: normalizedStart.toISOString(),
      endDate: normalizedEnd.toISOString(),
    };
  }, [startDateParam, endDateParam]);

  // Tab state
  const [activeTab, setActiveTab] = useState("vendor-requests");

  // Track which tabs have been loaded to prevent re-fetching
  const [hasFetchedVendorRequests, setHasFetchedVendorRequests] = useState(false);
  const [hasFetchedChangeRequests, setHasFetchedChangeRequests] = useState(false);

  // Track first load for loader display - initialize from cache
  const [firstLoadVendorRequests, setFirstLoadVendorRequests] = useState(tabCache.vendorRequests.firstLoad);
  const [firstLoadChangeRequests, setFirstLoadChangeRequests] = useState(tabCache.vendorChangeRequests.firstLoad);

  // Vendor requests state - initialize from cache if available
  const [data, setData] = useState(tabCache.vendorRequests.data || []);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const prevSearchTextRef = useRef("");
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [vendorToReject, setVendorToReject] = useState(null);

  // Vendor change requests state - initialize from cache if available
  const [changeRequestData, setChangeRequestData] = useState(tabCache.vendorChangeRequests.data || []);
  const [changeRequestLoading, setChangeRequestLoading] = useState(false);
  const [changeRequestPage, setChangeRequestPage] = useState(1);
  const [changeRequestTotalPages, setChangeRequestTotalPages] = useState(1);
  const [changeRequestViewDialogOpen, setChangeRequestViewDialogOpen] = useState(false);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState(null);
  const [changeRequestRejectDialogOpen, setChangeRequestRejectDialogOpen] = useState(false);
  const [changeRequestRejectReason, setChangeRequestRejectReason] = useState("");
  const [changeRequestToReject, setChangeRequestToReject] = useState(null);
  const [changeRequestActiveDropdown, setChangeRequestActiveDropdown] = useState(null);
  const [vendorRequestMenuAnchor, setVendorRequestMenuAnchor] = useState(null);
  const [changeRequestMenuAnchor, setChangeRequestMenuAnchor] = useState(null);
  const vendorRequestStatusPortalRef = useRef(null);
  const changeRequestStatusPortalRef = useRef(null);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);


  // Debounce search
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const getVendors = async (isSearching = false) => {
    const isFirstLoad = tabCache.vendorRequests.firstLoad;

    // Show loader on first load or when searching
    if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
      setIsLoading(true);
    }


    try {
      const requestPayload = {
        status,
        page,
        pageSize,
        searchtext: searchText,
      };

      if (dateRangeFromParams) {
        requestPayload.startDate = dateRangeFromParams.startDate;
        requestPayload.endDate = dateRangeFromParams.endDate;
      }

      const response = await VendorService.getAllVendorRequests(requestPayload);
      const vendorsData = response.data.data.requests;

      // Update state and cache
      setData(vendorsData);
      tabCache.vendorRequests.data = vendorsData;
      setTotalPages(response.data.data.pagination.pages);

      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.vendorRequests.firstLoad = false;
        setFirstLoadVendorRequests(false);
      }
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to fetch vendors");
      const emptyData = [];
      setData(emptyData);
      tabCache.vendorRequests.data = emptyData;
      if (isFirstLoad) {
        tabCache.vendorRequests.firstLoad = false;
        setFirstLoadVendorRequests(false);
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false);
      setIsFilterChanging(false);
    }
  };

  const getVendorChangeRequests = async () => {
    const isFirstLoad = tabCache.vendorChangeRequests.firstLoad;

    // Show loader only on first load
    if (isFirstLoad) {
      setChangeRequestLoading(true);
    }

    try {
      const response = await VendorService.getAllVendorChangeRequests({
        type: "discount", // Default type since filter is removed
        page: changeRequestPage,
        pageSize,
      });
      const changeRequestsData = response.data.data.requests;

      // Update state and cache
      setChangeRequestData(changeRequestsData);
      tabCache.vendorChangeRequests.data = changeRequestsData;
      setChangeRequestTotalPages(response.data.data.pagination.pages);

      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.vendorChangeRequests.firstLoad = false;
        setFirstLoadChangeRequests(false);
      }
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to fetch vendor change requests");
      const emptyData = [];
      setChangeRequestData(emptyData);
      tabCache.vendorChangeRequests.data = emptyData;
      if (isFirstLoad) {
        tabCache.vendorChangeRequests.firstLoad = false;
        setFirstLoadChangeRequests(false);
      }
    } finally {
      setChangeRequestLoading(false);
    }
  };

  // Update status from URL params
  useEffect(() => {
    const statusFromUrl = searchParams.get("status");
    if (statusFromUrl) {
      setIsFilterChanging(true);
      setStatus(statusFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    setIsFilterChanging(true);
    setPage(1);
  }, [searchParams]);

  // Track previous filter values to detect actual changes (per tab)
  const prevFiltersRef = React.useRef({
    vendorRequests: null, // null means not initialized yet
    changeRequests: null
  });

  // Fetch data when tab is first opened or when filters change
  useEffect(() => {
    if (activeTab === "vendor-requests") {
      const prevFilters = prevFiltersRef.current.vendorRequests;

      // Check if filters changed (only if we have previous values to compare)
      const filtersChanged = prevFilters !== null && (
        page !== prevFilters.page ||
        status !== prevFilters.status ||
        searchText !== prevFilters.searchText ||
        (dateRangeFromParams?.startDate !== prevFilters.dateRange?.startDate) ||
        (dateRangeFromParams?.endDate !== prevFilters.dateRange?.endDate)
      );

      // Check if search text changed (this indicates a search operation)
      const isSearchChange = prevSearchTextRef.current !== searchText;
      if (isSearchChange) {
        prevSearchTextRef.current = searchText;
      }

      // If we have cached data on first mount, it's already shown, just fetch in background
      // Otherwise, fetch normally (will show loader on first load)
      if (!hasFetchedVendorRequests || filtersChanged) {
        // Show loader if this is a search operation and we have cached data
        const isSearching = isSearchChange && hasFetchedVendorRequests;
        getVendors(isSearching);
        if (!hasFetchedVendorRequests) {
          setHasFetchedVendorRequests(true);
        }
        // Update previous filter values after fetch
        prevFiltersRef.current.vendorRequests = {
          page,
          status,
          searchText,
          dateRange: dateRangeFromParams
        };
      }
    } else if (activeTab === "vendor-change-requests") {
      const prevFilters = prevFiltersRef.current.changeRequests;

      // Check if filters changed (only if we have previous values to compare)
      const filtersChanged = prevFilters !== null && changeRequestPage !== prevFilters.page;

      // If we have cached data on first mount, it's already shown, just fetch in background
      // Otherwise, fetch normally (will show loader on first load)
      if (!hasFetchedChangeRequests || filtersChanged) {
        getVendorChangeRequests();
        if (!hasFetchedChangeRequests) {
          setHasFetchedChangeRequests(true);
        }
        // Update previous filter values after fetch
        prevFiltersRef.current.changeRequests = {
          page: changeRequestPage
        };
      }
    }
    // eslint-disable-next-line
  }, [page, pageSize, status, searchText, activeTab, changeRequestPage, dateRangeFromParams, hasFetchedVendorRequests, hasFetchedChangeRequests]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);   // 👈 START loader for pagination
    setPage(newPage);
  };


  const handleChangeRequestPageChange = (newPage) => {
    setChangeRequestPage(newPage);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setChangeRequestPage(1);
    setActiveDropdown(null);
    setVendorRequestMenuAnchor(null);
    setChangeRequestActiveDropdown(null);
    setChangeRequestMenuAnchor(null);
  };

  const handleChangeRequestView = (changeRequest) => {
    setSelectedChangeRequest(changeRequest);
    setChangeRequestViewDialogOpen(true);
  };

  const closeChangeRequestDialog = () => {
    setChangeRequestViewDialogOpen(false);
    setSelectedChangeRequest(null);
  };

  const handleChangeRequestStatusChange = async (rowId, newStatus) => {
    try {
      if (newStatus === "approved") {
        await VendorService.respondToVendorChangeRequest(rowId, "approved", "Request approved successfully.");
        Toast("success", "Vendor change request approved successfully");
        getVendorChangeRequests(); // Refresh the data
      } else if (newStatus === "rejected") {
        setChangeRequestToReject({
          id: rowId,
          name: changeRequestData.find(cr => cr._id === rowId)?.vendor?.vendor_name
        });
        setChangeRequestRejectDialogOpen(true);
        setChangeRequestActiveDropdown(null);
        setChangeRequestMenuAnchor(null);
        return;
      }
      setChangeRequestActiveDropdown(null);
      setChangeRequestMenuAnchor(null);
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to update vendor change request status");
    }
  };

  const handleChangeRequestRejectWithReason = async () => {
    if (!changeRequestRejectReason.trim()) {
      Toast("error", "Please provide a rejection reason");
      return;
    }

    try {
      await VendorService.respondToVendorChangeRequest(changeRequestToReject.id, "rejected", changeRequestRejectReason);
      Toast("success", "Vendor change request rejected successfully");
      setChangeRequestRejectDialogOpen(false);
      setChangeRequestRejectReason("");
      setChangeRequestToReject(null);
      setChangeRequestActiveDropdown(null);
      setChangeRequestMenuAnchor(null);
      getVendorChangeRequests(); // Refresh the data
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to reject vendor change request");
    }
  };

  const closeChangeRequestRejectDialog = () => {
    setChangeRequestRejectDialogOpen(false);
    setChangeRequestRejectReason("");
    setChangeRequestToReject(null);
    setChangeRequestActiveDropdown(null);
    setChangeRequestMenuAnchor(null);
  };


  const handleView = (vendor) => {
    setSelectedVendor(vendor);
    setViewDialogOpen(true);
  };

  const closeDialog = () => {
    setViewDialogOpen(false);
    setSelectedVendor(null);
  };

  const handleStatusChange = (option) => {
    if (!option || option.value === status) return;
    setIsFilterChanging(true);
    setStatus(option.value);
    setPage(1);
  };

  // Dropdown state for each row
  const [activeDropdown, setActiveDropdown] = useState(null);

  const handleVendorStatusChange = async (rowId, newStatus) => {
    try {
      if (newStatus === "approved") {
        await VendorService.approveVendorRequest(rowId);
        Toast("success", "Vendor request approved successfully");
        getVendors(); // Refresh the data
      } else if (newStatus === "rejected") {
        setVendorToReject({ id: rowId, name: data.find(v => v._id === rowId)?.vendor_name });
        setRejectDialogOpen(true);
        setActiveDropdown(null);
        setVendorRequestMenuAnchor(null);
        return;
      }
      setActiveDropdown(null);
      setVendorRequestMenuAnchor(null);
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to update vendor status");
    }
  };

  const handleRejectWithReason = async () => {
    if (!rejectReason.trim()) {
      Toast("error", "Please provide a rejection reason");
      return;
    }

    try {
      await VendorService.rejectVendorRequest(vendorToReject.id, rejectReason);
      Toast("success", "Vendor request rejected successfully");
      setRejectDialogOpen(false);
      setRejectReason("");
      setVendorToReject(null);
      setActiveDropdown(null);
      setVendorRequestMenuAnchor(null);
      getVendors(); // Refresh the data
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to reject vendor request");
    }
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectReason("");
    setVendorToReject(null);
    setActiveDropdown(null);
    setVendorRequestMenuAnchor(null);
  };

  useEffect(() => {
    const onDown = (event) => {
      const t = event.target;
      if (activeDropdown) {
        const inside =
          vendorRequestStatusPortalRef.current?.contains(t) ||
          t.closest("[data-vendor-request-status-trigger]");
        if (!inside) {
          setActiveDropdown(null);
          setVendorRequestMenuAnchor(null);
        }
      }
      if (changeRequestActiveDropdown) {
        const inside =
          changeRequestStatusPortalRef.current?.contains(t) ||
          t.closest("[data-change-request-status-trigger]");
        if (!inside) {
          setChangeRequestActiveDropdown(null);
          setChangeRequestMenuAnchor(null);
        }
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [activeDropdown, changeRequestActiveDropdown]);

  useEffect(() => {
    if (!activeDropdown && !changeRequestActiveDropdown) return;
    const close = () => {
      setActiveDropdown(null);
      setVendorRequestMenuAnchor(null);
      setChangeRequestActiveDropdown(null);
      setChangeRequestMenuAnchor(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [activeDropdown, changeRequestActiveDropdown]);

  const VendorStatusDropdown = ({
    status,
    rowId,
    onStatusChange,
    rowIndex,
    totalRows,
    searchText: vendorSearchText,
  }) => {
    const hasSearch = vendorSearchText.trim().length > 0;
    const isLastRow = totalRows > 0 && rowIndex === totalRows - 1;
    let openMenuAbove;
    if (hasSearch && totalRows === 1) {
      openMenuAbove = false;
    } else if (totalRows > 1) {
      openMenuAbove = isLastRow;
    } else {
      openMenuAbove = true;
    }

    if (status === "pending") {
      return (
        <div className="relative">
          <div
            data-vendor-request-status-trigger
            className="ms-center m-0 flex w-28 h-8 cursor-pointer items-center justify-between rounded-md bg-[#FF80001A]/10 text-[rgba(255,128,0,1)]  px-3 py-1 text-sm font-bold "
            onClick={(e) => {
              if (activeDropdown === rowId) {
                setActiveDropdown(null);
                setVendorRequestMenuAnchor(null);
              } else {
                const r = e.currentTarget.getBoundingClientRect();
                setVendorRequestMenuAnchor(
                  rowMenuAnchorFromTriggerRect(r, openMenuAbove)
                );
                setChangeRequestActiveDropdown(null);
                setChangeRequestMenuAnchor(null);
                setActiveDropdown(rowId);
              }
            }}
          >
            <span>Pending</span>
            <BsChevronDown className="h-2 w-2" style={{ strokeWidth: '2' }} />
          </div>
        </div>
      );
    }
    if (status === "approved") {
      return (
        <div className="ms-center w-28 h-8 m-0 inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-bold bg-[#15290E]/55 text-[#38B600]">
          <span>Approved</span>
        </div>
      );
    }
    if (status === "rejected") {
      return (
        <div className="ms-center w-28 h-8 m-0 inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-bold bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)]">
          <span>Rejected</span>
        </div>
      );
    }
    return null;
  };

  const ChangeRequestStatusDropdown = ({
    status,
    rowId,
    onStatusChange,
    rowIndex,
    totalRows,
  }) => {
    const isLastRow = totalRows > 0 && rowIndex === totalRows - 1;
    const openMenuAbove =
      totalRows > 1 ? isLastRow : true;

    if (status === "pending") {
      return (
        <div className="relative">
          <div
            data-change-request-status-trigger
            className="ms-center m-0 flex w-28 cursor-pointer items-center justify-between rounded-md bg-[#FF80001A]/10 text-[rgba(255,128,0,1)] px-3 py-1 text-sm font-bold "
            onClick={(e) => {
              if (changeRequestActiveDropdown === rowId) {
                setChangeRequestActiveDropdown(null);
                setChangeRequestMenuAnchor(null);
              } else {
                const r = e.currentTarget.getBoundingClientRect();
                setChangeRequestMenuAnchor(
                  rowMenuAnchorFromTriggerRect(r, openMenuAbove)
                );
                setActiveDropdown(null);
                setVendorRequestMenuAnchor(null);
                setChangeRequestActiveDropdown(rowId);
              }
            }}
          >
            <span>Pending</span>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </div>
        </div>
      );
    }
    if (status === "approved") {
      return (
        <div className="ms-center m-0 flex w-28 items-center justify-center rounded-md bg-[#15290E]/55 text-[#38B600] py-2 px-1.5 text-sm font-bold ">
          Approved
        </div>
      );
    }
    if (status === "rejected") {
      return (
        <div className="ms-center m-0 flex w-28 items-center justify-center rounded-md bg-[rgba(255,44,57,1)] text-white py-2 px-1.5 text-sm font-bold ">
          Rejected
        </div>
      );
    }
    return null;
  };

  return (
    <div className="-mt-2 grid h-full grid-cols-1 gap-2">
      <style>
        {`
          @media (min-width: 325px) and (max-width: 475px) {
            .vendor-tabs-card {
              width: 290px !important;
              max-width: 400px !important;
            }
          }
        `}
      </style>
      {/* Tabs */}
      <div className="col-span-1 h-auto w-auto rounded-lg 2xl:col-span-1">
        <Card extra={"w-full sm:w-[365px] h-auto px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg vendor-tabs-card"}>
          <div className="mb-0">
            <div className="flex items-center space-x-1.5 sm:space-x-2 max-w-fit">
              <button
                onClick={() => handleTabChange("vendor-requests")}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-all duration-200 outline-none focus:outline-none focus:ring-0 ${activeTab === "vendor-requests"
                  ? "bg-orangePrimary text-white   rounded-md"
                  : "text-white cursor-pointer border-none focus:border-transparent active:border-transparent"
                  }`}
              >
                Vendor Requests
              </button>
              <button
                onClick={() => handleTabChange("vendor-change-requests")}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-all duration-200 outline-none focus:outline-none focus:ring-0 ${activeTab === "vendor-change-requests"
                  ? "bg-orangePrimary text-white rounded-md "
                  : "text-white cursor-pointer border-none focus:border-transparent active:border-transparent"
                  }`}
              >
                Vendor Change Requests
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Tab Content */}
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full px-8 pb-8 overflow-x-auto"}>

          {/* Tab Content */}
          {activeTab === "vendor-requests" && (
            <>
              <div className="flex w-full flex-row items-center justify-between gap-3 mt-4 -ml-4">
                <div className="flex justify-start flex-shrink-0">
                  <div className="flex items-center rounded-full bg-navy-900 py-3 text-white w-[130px] sm:w-[180px] xl:w-[180px]">
                    <p className="pl-3 pr-2 text-xl">
                      <FiSearch className="h-4 w-4 text-white" />
                    </p>
                    <input
                      type="text"
                      placeholder="Search..."
                      className="block w-20 bg-navy-900 text-sm font-medium max-w-[120px] text-white outline-none  placeholder:!text-white sm:w-32"
                      onChange={handleSearchInputChange}
                      value={searchInputValue}
                    />
                  </div>
                </div>
                <div className="default flex h-full items-center justify-end flex-shrink-0 -mt-7 -mr-9">
                  <Select
                    className="ms-4 h-4 w-[145px] sm:w-[150px] !text-white"
                    classNamePrefix="select"
                    defaultValue={statusOptions[0]}
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find((option) => option.value === status)}
                    onChange={handleStatusChange}
                    styles={colourStyles}
                    isSearchable={false}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuShouldScrollIntoView={false}
                  />
                </div>
              </div>
              <div
                className={`table-scroll mt-4 relative ${isLoading
                  ? "h-[75vh] overflow-hidden"
                  : "overflow-x-auto"
                  }`}
              >
                {isLoading && (
                  <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                    <img src={Loader} alt="Loading..." className="h-16 w-16" />
                  </div>
                )}
                <table
                  className={`w-full ${isLoading ? "table-fixed" : ""}`}
                  style={
                    isLoading
                      ? { tableLayout: "fixed", width: "100%" }
                      : { tableLayout: "auto", minWidth: "900px" }
                  }
                >
                  <thead className={isLoading ? "hidden" : ""}>
                    <tr className="border-b border-white/10">
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Number</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Profile</th>
                      <th className="py-2 px-4 text-sm font-bold whitespace-nowrap uppercase text-white text-left align-top">Vendor Name</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Category</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Description</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Address</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Mobile</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Status</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr className="md:hidden">
                        <td colSpan={9} className="py-6">
                          <div className="flex justify-center">
                            <img src={Loader} alt="Loading..." className="h-16 w-16" />
                          </div>
                        </td>
                      </tr>
                    ) : data && data.length > 0 ? (
                      data.map((row, idx) => (
                        <tr key={row._id} className="border-b border-[#fbfbfb1a] last:border-none">
                          <td className="py-2 px-4 pt-4 text-left align-top">{(page - 1) * pageSize + idx + 1}</td>
                          <td className="py-2 px-4 pt-4 text-left align-top">
                            <img
                              src={row.profile_image}
                              alt={row.vendor_name}
                              className="h-8 w-8 rounded-lg object-cover"
                            />
                          </td>
                          <td className="py-2 px-4 pt-4 font-bold text-white text-left align-top">
                            <div className="flex items-center gap-2">
                              <span>{row.vendor_name}</span>
                              {row.is_custom && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-purple-600/20 text-purple-300 border-purple-500/30">
                                  Custom
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-4 pt-4 text-white text-left align-top">{row.category_name || '--'}</td>
                          <td className="py-2 px-4 pt-4 text-white text-left align-top">
                            <div className="line-clamp-2">{row.description || '--'}</div>
                          </td>
                          <td className="py-2 px-4 pt-4 text-white text-left align-top">
                            <div className="line-clamp-2">{row.address}</div>
                          </td>
                          <td className="py-2 px-4 pt-4 text-white text-left align-top">{row.mobile_number}</td>
                          <td className="py-2 px-4 pt-4 text-left align-top">
                            <VendorStatusDropdown
                              status={row.status}
                              rowId={row._id}
                              rowIndex={idx}
                              totalRows={data.length}
                              searchText={searchText}
                              onStatusChange={(newStatus) => handleVendorStatusChange(row._id, newStatus)}
                            />
                          </td>
                          <td className="py-2 px-4 pt-4 text-left align-top">
                            <div
                              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 ml-2"
                              onClick={() => handleView(row)}
                              title="View"
                            >
                              <IoEye />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="py-4 text-center">
                          <p className="my-3">Data Not Found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {activeDropdown &&
                vendorRequestMenuAnchor &&
                data.find((v) => v._id === activeDropdown)?.status === "pending" &&
                createPortal(
                  <div
                    ref={vendorRequestStatusPortalRef}
                    className="fixed z-[10000] w-28 rounded-md border border-white/10 bg-navy-800 px-3 py-1.5 shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                    style={getRowMenuPortalStyle(vendorRequestMenuAnchor)}
                  >
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                      onClick={() => handleVendorStatusChange(activeDropdown, "approved")}
                    >
                      Approve
                    </button>
                    <div className="my-1 border-t border-white/10" />
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                      onClick={() => handleVendorStatusChange(activeDropdown, "rejected")}
                    >
                      Reject
                    </button>
                  </div>,
                  document.body
                )}

              {data && data.length > 0 && !isLoading && (
                <Pagination page={page} totalPages={totalPages} handlePageChange={handlePageChange} />
              )}
            </>
          )}

          {/* Vendor Change Requests Tab */}
          {activeTab === "vendor-change-requests" && (
            <>
              <div
                className={`table-scroll mt-4 relative ${changeRequestLoading
                  ? "h-[65vh] overflow-hidden"
                  : "overflow-x-auto"
                  }`}
              >
                {changeRequestLoading && (
                  <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                    <img src={Loader} alt="Loading..." className="h-16 w-16" />
                  </div>
                )}
                <table
                  className={`w-full ${changeRequestLoading ? "table-fixed" : ""}`}
                  style={
                    changeRequestLoading
                      ? { tableLayout: "fixed", width: "100%" }
                      : { tableLayout: "auto", minWidth: "900px" }
                  }
                >
                  <thead className={changeRequestLoading ? "hidden" : ""}>
                    <tr className="border-b border-white/10">
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Number</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Vendor Name</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Type</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Current Discount</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Requested Discount</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Reason</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Status</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Admin Response</th>
                      <th className="py-2 px-4 text-sm font-bold uppercase text-white text-left align-top">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeRequestLoading ? (
                      <tr className="md:hidden">
                        <td colSpan={9} className="py-6">
                          <div className="flex justify-center">
                            <img src={Loader} alt="Loading..." className="h-16 w-16" />
                          </div>
                        </td>
                      </tr>
                    ) : changeRequestData && changeRequestData.length > 0 ? (
                      changeRequestData.map((row, idx) => (
                        <tr key={row._id} className="border-b border-[#fbfbfb1a] last:border-none">
                          <td className="py-2 px-4 text-left align-top">{(changeRequestPage - 1) * pageSize + idx + 1}</td>
                          <td className="py-2 px-4 font-bold text-white text-left align-top">
                            {row.vendor?.vendor_name || '--'}
                          </td>
                          <td className="py-2 px-4 text-white text-left align-top capitalize">{row.type || '--'}</td>
                          <td className="py-2 px-4 text-white text-left align-top">{row.current_value || '--'}</td>
                          <td className="py-2 px-4 text-white text-left align-top">{row.requested_value || '--'}</td>
                          <td className="py-2 px-4 pt-4 text-white text-left align-top">
                            <div className="line-clamp-2">{row.reason || '--'}</div>
                          </td>
                          <td className="py-2 px-4 text-left align-top">
                            <ChangeRequestStatusDropdown
                              status={row.status}
                              rowId={row._id}
                              rowIndex={idx}
                              totalRows={changeRequestData.length}
                              onStatusChange={(newStatus) => handleChangeRequestStatusChange(row._id, newStatus)}
                            />
                          </td>
                          <td className="py-2 px-4 text-white text-left align-top">
                            <div className="line-clamp-2">{row.admin_response || '--'}</div>
                          </td>
                          <td className="py-2 px-4 text-left align-top">
                            <div
                              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 ml-2"
                              onClick={() => handleChangeRequestView(row)}
                              title="View"
                            >
                              <IoEye />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="py-4 text-center">
                          <p className="my-3">Data Not Found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {changeRequestActiveDropdown &&
                changeRequestMenuAnchor &&
                changeRequestData.find((r) => r._id === changeRequestActiveDropdown)?.status ===
                  "pending" &&
                createPortal(
                  <div
                    ref={changeRequestStatusPortalRef}
                    className="fixed z-[10000] w-28 max-h-[120px] overflow-y-auto rounded-md border border-white/10 bg-navy-800 px-1.5 py-1.5 shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                    style={getRowMenuPortalStyle(changeRequestMenuAnchor)}
                  >
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                      onClick={() =>
                        handleChangeRequestStatusChange(
                          changeRequestActiveDropdown,
                          "approved"
                        )
                      }
                    >
                      Approve
                    </button>
                    <div className="my-1 border-t border-white/10" />
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/5"
                      onClick={() =>
                        handleChangeRequestStatusChange(
                          changeRequestActiveDropdown,
                          "rejected"
                        )
                      }
                    >
                      Reject
                    </button>
                  </div>,
                  document.body
                )}

              {changeRequestData && changeRequestData.length > 0 && !changeRequestLoading && (
                <Pagination page={changeRequestPage} totalPages={changeRequestTotalPages} handlePageChange={handleChangeRequestPageChange} />
              )}
            </>
          )}
        </Card>
      </div>

      {/* View Dialog */}
      {viewDialogOpen && selectedVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-lg rounded-xl bg-navy-800 p-6 shadow-lg relative">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeDialog}
            >
              &times;
            </button>
            <div className="flex flex-col items-center mb-6">
              <img
                src={selectedVendor.profile_image}
                alt={selectedVendor.vendor_name}
                className="h-24 w-24 rounded-full object-cover border-4 border-gray-700 mb-3"
              />
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold text-white">{selectedVendor.vendor_name || '--'}</h2>
                {selectedVendor.is_custom && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-purple-600/20 text-purple-300 border-purple-500/30">
                    Custom
                  </span>
                )}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold mb-2 ${selectedVendor.status === 'approved' ? 'bg-[#38B600] text-white' : selectedVendor.status === 'pending' ? 'bg-[rgba(255,128,0,1)] text-white' : 'bg-[rgba(255,44,57,1)] text-white'}`}>{selectedVendor.status || '--'}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Category:</span>
                <div className="text-gray-600">{selectedVendor.category_name || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Mobile:</span>
                <div className="text-gray-600">{selectedVendor.mobile_number || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Email:</span>
                <div className="text-gray-600 break-all">{selectedVendor.email || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Website:</span>
                <div className="text-gray-600 break-all">
                  {selectedVendor.website_url ? (
                    <a href={selectedVendor.website_url} target="_blank" rel="noopener noreferrer" className="text-orangePrimary underline">{selectedVendor.website_url}</a>
                  ) : '--'}
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Created At:</span>
                <div className="text-gray-600">{selectedVendor.createdAt ? new Date(selectedVendor.createdAt).toLocaleString() : '--'}</div>
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <span className="font-bold text-white mb-1">Address:</span>
                <div className="text-gray-600">{selectedVendor.address || '--'}</div>
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <span className="font-bold text-white mb-1">Description:</span>
                <div className="text-gray-600">{selectedVendor.description || '--'}</div>
              </div>
              {/* Add more fields here if present in selectedVendor */}
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectDialogOpen && vendorToReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg relative">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeRejectDialog}
            >
              &times;
            </button>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Reject Vendor Request</h2>
              <p className="text-gray-300 text-sm">
                Rejecting vendor: <span className="font-semibold text-white">{vendorToReject.name}</span>
              </p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-bold text-white mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                className="w-full h-24 px-3 py-2 bg-blue-800/10 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary resize-none"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeRejectDialog}
                className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectWithReason}
                className="px-4 py-2 bg-red-600/60 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Change Request View Dialog */}
      {changeRequestViewDialogOpen && selectedChangeRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-navy-800 p-6 shadow-lg relative">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeChangeRequestDialog}
            >
              &times;
            </button>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Vendor Change Request Details</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Vendor Name:</span>
                <div className="text-gray-600">{selectedChangeRequest.vendor?.vendor_name || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Type:</span>
                <div className="text-gray-600 capitalize">{selectedChangeRequest.type || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Current Discount:</span>
                <div className="text-gray-600">{selectedChangeRequest.current_value || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Requested Discount:</span>
                <div className="text-gray-600">{selectedChangeRequest.requested_value || '--'}</div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Status:</span>
                <div className="mt-0.5">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${selectedChangeRequest.status === 'approved' ? 'bg-[#38B600] text-white' :
                    selectedChangeRequest.status === 'pending' ? 'bg-[rgba(255,128,0,1)] text-white' :
                      'bg-[rgba(255,44,57,1)] text-white'
                    }`}>
                    {selectedChangeRequest.status || '--'}
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Created At:</span>
                <div className="text-gray-600">
                  {selectedChangeRequest.createdAt ? new Date(selectedChangeRequest.createdAt).toLocaleString() : '--'}
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white mb-1">Processed At:</span>
                <div className="text-gray-600">
                  {selectedChangeRequest.processed_at ? new Date(selectedChangeRequest.processed_at).toLocaleString() : '--'}
                </div>
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <span className="font-bold text-white mb-1">Reason:</span>
                <div className="text-gray-600">{selectedChangeRequest.reason || '--'}</div>
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <span className="font-bold text-white  mb-1">Admin Response:</span>
                <div className="text-gray-600">{selectedChangeRequest.admin_response || '--'}</div>
              </div>
              {selectedChangeRequest.account && (
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 -mt-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-white  mb-1">Account Username:</span>
                    <div className="text-gray-600">{selectedChangeRequest.account.username || '--'}</div>
                  </div>
                  <div className="flex flex-col sm:-mt-24">
                    <span className="font-bold text-white  mb-1 ">Account Email:</span>
                    <div className="text-gray-600">{selectedChangeRequest.account.email || '--'}</div>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-white  mb-1">Account Phone:</span>
                    <div className="text-gray-600">{selectedChangeRequest.account.phone || '--'}</div>
                  </div>
                  <div className="flex flex-col sm:-mt-56">
                    <span className="font-bold text-white mb-1 ">Account Name:</span>
                    <div className="text-gray-600">
                      {selectedChangeRequest.account.firstname && selectedChangeRequest.account.lastname
                        ? `${selectedChangeRequest.account.firstname} ${selectedChangeRequest.account.lastname}`
                        : '--'
                      }
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vendor Change Request Reject Dialog */}
      {changeRequestRejectDialogOpen && changeRequestToReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg relative">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeChangeRequestRejectDialog}
            >
              &times;
            </button>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Reject Vendor Change Request</h2>
              <p className="text-gray-300 text-sm">
                Rejecting change request for: <span className="font-semibold text-white">{changeRequestToReject.name}</span>
              </p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-bold text-white mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={changeRequestRejectReason}
                onChange={(e) => setChangeRequestRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                className="w-full h-24 px-3 py-2 bg-navy-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary resize-none"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeChangeRequestRejectDialog}
                className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeRequestRejectWithReason}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendor; 